import API from "@/axios/API";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { LineChart } from "@mui/x-charts/LineChart";
import { useCallback, useEffect, useRef, useState } from "react";
import { DARK_THEME, meterSeverity, statusRole } from "./theme";

const MONITORING_BASE = `${import.meta.env.VITE_BACKEND_API || import.meta.env.BACKEND_API || ""}/auth/admin/monitoring`;
const POLL_MS = 10000;
const MAX_TREND_POINTS = 30;
const LIVE_LIMIT = 200;

/* ------------------------------------------------------------------ */
/*  helpers                                                            */
/* ------------------------------------------------------------------ */
const severityColor = (severity) => {
    if (severity === "critical") return DARK_THEME.status.critical;
    if (severity === "warning") return DARK_THEME.status.warning;
    return DARK_THEME.accent;
};

const statusChipColor = (role) => {
    if (role === "good") return DARK_THEME.status.good;
    if (role === "warning") return DARK_THEME.status.warning;
    if (role === "critical") return DARK_THEME.status.critical;
    return DARK_THEME.textSecondary; // neutral (3xx)
};

const percentile = (sortedValues, p) => {
    if (!sortedValues.length) return 0;
    const idx = Math.min(sortedValues.length - 1, Math.max(0, Math.ceil((p / 100) * sortedValues.length) - 1));
    return sortedValues[idx];
};

const fmtMs = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`);
const fmtTime = (v) => {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
};

/** Derive throughput / percentiles / error rate / top lists from a raw live-request batch. */
const deriveFromLive = (requests) => {
    if (!requests.length) {
        return { throughputPerMin: 0, p50: 0, p95: 0, p99: 0, errorRate: 0, topApis: [], slowest: [] };
    }
    const durations = requests.map((r) => r.durationMs ?? 0).sort((a, b) => a - b);
    const errorCount = requests.filter((r) => (r.httpStatus ?? 0) >= 400).length;

    const times = requests.map((r) => new Date(r.startTime).getTime()).filter((t) => !Number.isNaN(t));
    const windowMs = times.length > 1 ? Math.max(1000, Math.max(...times) - Math.min(...times)) : 60000;
    const throughputPerMin = requests.length / (windowMs / 60000);

    const byRoute = new Map();
    requests.forEach((r) => {
        const key = `${r.method} ${r.path}`;
        byRoute.set(key, (byRoute.get(key) || 0) + 1);
    });
    const topApis = [...byRoute.entries()]
        .map(([key, count]) => ({ key, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

    const slowest = [...requests]
        .sort((a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0))
        .slice(0, 5);

    return {
        throughputPerMin,
        p50: percentile(durations, 50),
        p95: percentile(durations, 95),
        p99: percentile(durations, 99),
        errorRate: errorCount / requests.length,
        topApis,
        slowest,
    };
};

/* ------------------------------------------------------------------ */
/*  small building blocks                                              */
/* ------------------------------------------------------------------ */
const Card = ({ title, children, sx }) => (
    <Box
        sx={{
            bgcolor: DARK_THEME.surface,
            border: `1px solid ${DARK_THEME.border}`,
            borderRadius: `${DARK_THEME.radius.card}px`,
            p: 2,
            ...sx,
        }}
    >
        {title && (
            <Typography
                sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    color: DARK_THEME.textMuted,
                    mb: 1.5,
                }}
            >
                {title}
            </Typography>
        )}
        {children}
    </Box>
);

const StatTile = ({ label, value, sub }) => (
    <Card sx={{ flex: 1, minWidth: 150 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: DARK_THEME.textMuted, textTransform: "uppercase" }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 700, color: DARK_THEME.textPrimary, mt: 0.5, fontFamily: DARK_THEME.mono }}>
            {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: 11.5, color: DARK_THEME.textSecondary, mt: 0.25 }}>{sub}</Typography>}
    </Card>
);

const MethodChip = ({ method }) => (
    <Box
        component="span"
        sx={{
            fontFamily: DARK_THEME.mono,
            fontSize: 10.5,
            fontWeight: 700,
            color: DARK_THEME.textSecondary,
            border: `1px solid ${DARK_THEME.border}`,
            borderRadius: "4px",
            px: 0.6,
            py: 0.1,
            mr: 0.75,
        }}
    >
        {method}
    </Box>
);

const StatusDot = ({ httpStatus }) => {
    const role = statusRole(httpStatus);
    return (
        <Tooltip title={`HTTP ${httpStatus}`} arrow>
            <Box
                component="span"
                sx={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: statusChipColor(role),
                    mr: 1,
                    flexShrink: 0,
                }}
            />
        </Tooltip>
    );
};

/* ------------------------------------------------------------------ */
/*  main tab                                                           */
/* ------------------------------------------------------------------ */
export default function DashboardTab() {
    const [snapshot, setSnapshot] = useState(null);
    const [derived, setDerived] = useState(deriveFromLive([]));
    const [trend, setTrend] = useState([]); // [{ t, throughput, p95, cpuLoad, heapUsedMb }]
    const [lastUpdated, setLastUpdated] = useState(null);
    const [paused, setPaused] = useState(false);
    const [error, setError] = useState(null);
    const pausedRef = useRef(paused);
    pausedRef.current = paused;

    const tick = useCallback(async () => {
        if (pausedRef.current) return;
        try {
            const [snapRes, liveRes] = await Promise.all([
                API.get(`${MONITORING_BASE}/resources/latest`),
                API.get(`${MONITORING_BASE}/requests/live`, { params: { limit: LIVE_LIMIT } }),
            ]);
            const snap = snapRes.data || null;
            const requests = liveRes.data || [];
            const d = deriveFromLive(requests);

            setSnapshot(snap);
            setDerived(d);
            setError(null);
            setLastUpdated(new Date());

            setTrend((prev) => {
                const next = [
                    ...prev,
                    {
                        t: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }),
                        throughput: Math.round(d.throughputPerMin * 10) / 10,
                        p95: Math.round(d.p95),
                        cpuLoad: snap?.cpuLoad != null && snap.cpuLoad >= 0 ? Math.round(snap.cpuLoad * 1000) / 10 : null,
                        heapUsedMb: snap?.heapUsedMb ?? null,
                    },
                ];
                return next.slice(-MAX_TREND_POINTS);
            });
        } catch (err) {
            setError(err.message || "Failed to load monitoring data.");
        }
    }, []);

    useEffect(() => {
        tick();
        const interval = setInterval(tick, POLL_MS);
        return () => clearInterval(interval);
    }, [tick]);

    const cpuPercent = snapshot?.cpuLoad != null && snapshot.cpuLoad >= 0 ? Math.round(snapshot.cpuLoad * 100) : null;
    const heapRatio = snapshot?.heapUsedMb != null && snapshot?.heapMaxMb > 0 ? snapshot.heapUsedMb / snapshot.heapMaxMb : null;
    const heapPercent = heapRatio != null ? Math.round(heapRatio * 100) : null;

    const cpuColor = severityColor(meterSeverity((cpuPercent ?? 0) / 100));
    const heapColor = severityColor(meterSeverity(heapRatio ?? 0));

    const gaugeSx = (color) => ({
        [`& .${gaugeClasses.valueArc}`]: { fill: color },
        [`& .${gaugeClasses.referenceArc}`]: { fill: DARK_THEME.accentTrack },
        [`& .${gaugeClasses.valueText}`]: { fill: DARK_THEME.textPrimary, fontSize: 22, fontWeight: 700 },
    });

    return (
        <Box
            sx={{
                bgcolor: DARK_THEME.page,
                borderRadius: `${DARK_THEME.radius.cardLg}px`,
                p: 2.5,
                fontFamily: DARK_THEME.font,
            }}
        >
            {/* toolbar */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Tooltip title={paused ? "Resume live updates" : "Pause live updates"} arrow>
                        <IconButton
                            size="small"
                            onClick={() => setPaused((p) => !p)}
                            sx={{ color: DARK_THEME.textPrimary, border: `1px solid ${DARK_THEME.border}` }}
                        >
                            {paused ? <PlayArrowRoundedIcon sx={{ fontSize: 18 }} /> : <PauseRoundedIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Refresh now" arrow>
                        <IconButton size="small" onClick={tick} sx={{ color: DARK_THEME.textPrimary, border: `1px solid ${DARK_THEME.border}` }}>
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Typography sx={{ fontSize: 12, color: DARK_THEME.textSecondary }}>
                        {paused ? "Paused" : `Auto-refreshing every ${POLL_MS / 1000}s`}
                        {lastUpdated && ` · last updated ${fmtTime(lastUpdated)}`}
                    </Typography>
                </Stack>
                {error && (
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: DARK_THEME.status.critical }} />
                        <Typography sx={{ fontSize: 12, color: DARK_THEME.status.critical }}>{error}</Typography>
                    </Stack>
                )}
            </Stack>

            {/* stat strip */}
            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", mb: 2 }}>
                <StatTile label="Throughput" value={`${derived.throughputPerMin.toFixed(1)}/min`} sub={`last ${LIVE_LIMIT} requests`} />
                <StatTile label="Error Rate" value={`${(derived.errorRate * 100).toFixed(1)}%`} />
                <StatTile label="Threads" value={snapshot?.threadCount ?? "—"} />
                <StatTile
                    label="Mongo Pool"
                    value={snapshot ? `${snapshot.mongoPoolActive}/${snapshot.mongoPoolActive + snapshot.mongoPoolAvailable}` : "—"}
                    sub="active / total"
                />
            </Stack>

            {/* gauges + gc/heap stats */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Card title="CPU" sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                    <Gauge
                        width={140}
                        height={140}
                        value={cpuPercent ?? 0}
                        valueMin={0}
                        valueMax={100}
                        startAngle={-110}
                        endAngle={110}
                        innerRadius="75%"
                        outerRadius="100%"
                        cornerRadius="50%"
                        text={cpuPercent == null ? "N/A" : `${cpuPercent}%`}
                        sx={gaugeSx(cpuColor)}
                    />
                </Card>

                <Card title="Heap Memory" sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                    <Gauge
                        width={140}
                        height={140}
                        value={heapPercent ?? 0}
                        valueMin={0}
                        valueMax={100}
                        startAngle={-110}
                        endAngle={110}
                        innerRadius="75%"
                        outerRadius="100%"
                        cornerRadius="50%"
                        text={heapPercent == null ? "N/A" : `${heapPercent}%`}
                        sx={gaugeSx(heapColor)}
                    />
                    <Typography sx={{ fontSize: 11.5, color: DARK_THEME.textSecondary, mt: 1 }}>
                        {snapshot ? `${snapshot.heapUsedMb} / ${snapshot.heapMaxMb} MB` : "—"}
                    </Typography>
                </Card>

                <Card title="Host Details" sx={{ flex: 1.2 }}>
                    <Stack spacing={1}>
                        {[
                            ["Host", snapshot?.hostId ?? "—"],
                            ["Non-heap used", snapshot ? `${snapshot.nonHeapUsedMb} MB` : "—"],
                            ["GC pause (since last sample)", snapshot ? `${snapshot.gcPauseMsDelta} ms` : "—"],
                            ["p50 / p95 / p99 latency", `${fmtMs(derived.p50)} / ${fmtMs(derived.p95)} / ${fmtMs(derived.p99)}`],
                        ].map(([label, value]) => (
                            <Stack key={label} direction="row" justifyContent="space-between">
                                <Typography sx={{ fontSize: 12, color: DARK_THEME.textSecondary }}>{label}</Typography>
                                <Typography sx={{ fontSize: 12, color: DARK_THEME.textPrimary, fontFamily: DARK_THEME.mono }}>{value}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Card>
            </Stack>

            {/* trend charts — one axis each, single series, per dataviz method */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                <Card title="Throughput (requests / min)" sx={{ flex: 1 }}>
                    <LineChart
                        height={200}
                        series={[{ data: trend.map((t) => t.throughput), color: DARK_THEME.accent, showMark: false, curve: "monotone", area: true, label: "req/min" }]}
                        xAxis={[{ data: trend.map((t) => t.t), scaleType: "point", tickLabelStyle: { fontSize: 10, fill: DARK_THEME.textMuted } }]}
                        yAxis={[{ tickLabelStyle: { fontSize: 10, fill: DARK_THEME.textMuted } }]}
                        sx={{
                            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: DARK_THEME.baseline },
                            "& .MuiChartsGrid-line": { stroke: DARK_THEME.gridline },
                        }}
                        grid={{ horizontal: true }}
                        slotProps={{ legend: { hidden: true } }}
                    />
                </Card>
                <Card title="p95 Latency (ms)" sx={{ flex: 1 }}>
                    <LineChart
                        height={200}
                        series={[{ data: trend.map((t) => t.p95), color: DARK_THEME.accent, showMark: false, curve: "monotone", area: true, label: "p95 ms" }]}
                        xAxis={[{ data: trend.map((t) => t.t), scaleType: "point", tickLabelStyle: { fontSize: 10, fill: DARK_THEME.textMuted } }]}
                        yAxis={[{ tickLabelStyle: { fontSize: 10, fill: DARK_THEME.textMuted } }]}
                        sx={{
                            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: DARK_THEME.baseline },
                            "& .MuiChartsGrid-line": { stroke: DARK_THEME.gridline },
                        }}
                        grid={{ horizontal: true }}
                        slotProps={{ legend: { hidden: true } }}
                    />
                </Card>
            </Stack>

            {/* top APIs / slowest requests */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <Card title="Top APIs by Traffic" sx={{ flex: 1 }}>
                    {derived.topApis.length === 0 && (
                        <Typography sx={{ fontSize: 12.5, color: DARK_THEME.textMuted }}>No traffic in the current window.</Typography>
                    )}
                    <Stack spacing={1.25}>
                        {derived.topApis.map((api) => {
                            const max = derived.topApis[0]?.count || 1;
                            return (
                                <Box key={api.key}>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                                        <Typography sx={{ fontSize: 12, color: DARK_THEME.textPrimary, fontFamily: DARK_THEME.mono, wordBreak: "break-all" }}>
                                            {api.key}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: DARK_THEME.textSecondary, ml: 1, flexShrink: 0 }}>{api.count}</Typography>
                                    </Stack>
                                    <Box sx={{ height: 6, bgcolor: DARK_THEME.gridline, borderRadius: "999px", overflow: "hidden" }}>
                                        <Box sx={{ height: "100%", width: `${(api.count / max) * 100}%`, bgcolor: DARK_THEME.accent, borderRadius: "999px" }} />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Card>

                <Card title="Slowest Recent Requests" sx={{ flex: 1 }}>
                    {derived.slowest.length === 0 && (
                        <Typography sx={{ fontSize: 12.5, color: DARK_THEME.textMuted }}>No requests in the current window.</Typography>
                    )}
                    <Stack spacing={1}>
                        {derived.slowest.map((r) => (
                            <Stack key={r.id || r.traceId} direction="row" alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
                                    <StatusDot httpStatus={r.httpStatus} />
                                    <MethodChip method={r.method} />
                                    <Typography
                                        sx={{ fontSize: 12, color: DARK_THEME.textPrimary, fontFamily: DARK_THEME.mono, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    >
                                        {r.path}
                                    </Typography>
                                </Stack>
                                <Chip
                                    size="small"
                                    label={fmtMs(r.durationMs)}
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: 11,
                                        fontFamily: DARK_THEME.mono,
                                        fontWeight: 700,
                                        bgcolor: "rgba(255,255,255,0.06)",
                                        color: DARK_THEME.textPrimary,
                                    }}
                                />
                            </Stack>
                        ))}
                    </Stack>
                </Card>
            </Stack>
        </Box>
    );
}
